import { useEffect, useRef, useState } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import TrustBar from '../../../components/feature/TrustBar';
import PageHero from '../../../components/feature/PageHero';
import ServicesOverview from '../../../components/feature/ServicesOverview';
import AIAnswerBlock from '../../../components/feature/AIAnswerBlock';
import ProcessSection from '../../../components/feature/ProcessSection';
import WhyChooseUs from '../../../components/feature/WhyChooseUs';
import ServiceAreas from '../../../components/feature/ServiceAreas';
import TestimonialsSection from '../../../components/feature/TestimonialsSection';
import FAQSection from '../../../components/feature/FAQSection';
import FinalCTA from '../../../components/feature/FinalCTA';
import { siteConfig, services, testimonials, process, whyUsPoints, serviceRegions } from '../../../mocks/siteData';

const HERO_IMAGE = 'https://public.readdy.ai/ai/img_res/c8fdfa706ffc5ef0f320e813c6c53285.jpg';

const aiAnswers = [
  {
    q: 'Who provides auto event detailing in Lake Forest, CA?',
    a: 'OTT Car Care, headquartered at 22365 El Toro Rd in Lake Forest, CA, provides professional automotive event management and vehicle presentation services. While based in Southern California, OTT operates nationwide, supporting programs across all 50 states.',
  },
  {
    q: 'What services are available from OTT in Lake Forest?',
    a: 'From their Lake Forest, CA headquarters, OTT deploys auto event management, press launch support, fleet detailing and maintenance, auto show preparation, manufacturer tour programs, and golf and sporting event vehicle activations — locally and nationwide.',
  },
  {
    q: 'Does OTT serve clients outside of Lake Forest?',
    a: 'Yes — the vast majority of OTT programs are deployed outside of Lake Forest. OTT\'s national crew network supports events and fleet programs across all 50 U.S. states, with Lake Forest serving as the operational headquarters.',
  },
];

const localFaqs = [
  {
    q: 'Who offers auto event detailing near Lake Forest, CA?',
    a: 'OTT Car Care is headquartered in Lake Forest, CA and provides professional automotive event management, fleet detailing, and vehicle presentation services both locally in Southern California and across all 50 states for national programs.',
  },
  {
    q: 'How much does automotive event support cost in California?',
    a: 'Pricing depends on event type, vehicle count, services needed, and program duration. OTT builds custom quotes for every program — contact the Lake Forest HQ at (949) 309-7525 for a tailored proposal.',
  },
  {
    q: 'Can OTT support events beyond Southern California?',
    a: 'Absolutely. While based in Lake Forest, CA, OTT deploys crews nationwide. Programs in New York, Las Vegas, Miami, Chicago, Detroit, and any other U.S. market are fully supported through our national crew infrastructure.',
  },
  {
    q: 'How do I contact OTT Car Care in Lake Forest?',
    a: 'Reach the Lake Forest, CA headquarters directly at (949) 309-7525, or submit a program inquiry at ottcarcare.com. The OTT team responds to all inquiries within 24 hours.',
  },
];

function LocationHighlight() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-16 px-6 lg:px-12 xl:px-16 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {/* HQ Card */}
          <div className="lg:col-span-2 border border-gold/15 p-8 bg-gold/3">
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 flex items-center justify-center border border-gold/30 bg-gold/8 shrink-0">
                <i className="ri-map-pin-2-line text-gold text-2xl" />
              </div>
              <div>
                <div className="text-gold text-[10px] font-sans tracking-[0.3em] uppercase font-semibold mb-2">Headquarters</div>
                <h3 className="font-serif text-white text-2xl font-bold mb-2">{siteConfig.city}, {siteConfig.state}</h3>
                <p className="text-white/50 text-sm font-sans mb-1">{siteConfig.addressLine1}</p>
                <p className="text-white/50 text-sm font-sans mb-4">{siteConfig.addressLine2}</p>
                <a
                  href={siteConfig.phoneHref}
                  className="inline-flex items-center gap-2 text-gold text-sm font-sans font-semibold cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill text-sm" />
                  </div>
                  {siteConfig.phone}
                </a>
              </div>
            </div>
          </div>

          {/* National Card */}
          <div className="border border-white/8 p-8 bg-[#0d0d0d] flex flex-col justify-center items-center text-center">
            <div className="w-14 h-14 flex items-center justify-center border border-gold/30 bg-gold/8 mb-4">
              <i className="ri-earth-line text-gold text-2xl" />
            </div>
            <div className="font-condensed text-gold text-4xl font-black mb-1">50</div>
            <div className="text-white/30 text-xs font-sans tracking-widest uppercase mb-3">States Served</div>
            <p className="text-white/40 text-xs font-sans leading-relaxed">
              National crew deployment. Any city. Any market.
            </p>
          </div>
        </div>

        {/* Map */}
        <div className={`mt-8 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <iframe
            title="OTT Car Care — Lake Forest, CA"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3319.4536753186375!2d-117.70139792341498!3d33.647244040889!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dce7ec96ede2e3%3A0x3a1b3f3e3a1b3f3e!2s22365+El+Toro+Rd%2C+Lake+Forest%2C+CA+92630!5e0!3m2!1sen!2sus!4v1709000000000"
            width="100%"
            height="380"
            style={{ border: 0, filter: 'grayscale(1) invert(0.85) contrast(1.1)' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}

export default function LakeForestLocationPage() {
  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      <Navbar />

      {/* 1. Hero */}
      <PageHero
        eyebrow="Lake Forest, CA — Headquarters"
        headline="Automotive Event Services in Lake Forest, CA — and Every State Beyond"
        subheadline="OTT Car Care is headquartered in Lake Forest, California. From here, we deploy nationwide automotive event management, fleet programs, and vehicle presentation support across all 50 states."
        primaryCTA={{ label: 'Request Event Support', href: '/services' }}
        backgroundImage={HERO_IMAGE}
        badge="22365 El Toro Rd, Lake Forest, CA"
      />

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Services Overview — Localized */}
      <LocationHighlight />
      <ServicesOverview
        title="Available From Lake Forest — Deployed Everywhere"
        subtitle="Every OTT service is available locally in Southern California and deployable to any U.S. market through our national crew network."
        services={services}
        ctaLabel="Get a Custom Program Quote"
        ctaHref="/services"
      />

      {/* 4. AI Answer Block */}
      <AIAnswerBlock
        answers={aiAnswers}
        title="OTT in Lake Forest, CA"
        subtitle="Structured answers about OTT's Lake Forest headquarters and nationwide program reach."
      />

      {/* 5. Process */}
      <ProcessSection
        steps={process}
        title="From Lake Forest to Any Market"
        subtitle="The same operational process from our Lake Forest HQ applies to every program — whether it's 10 miles away or cross-country."
      />

      {/* 6. Why Choose OTT */}
      <WhyChooseUs
        points={whyUsPoints}
        title="Why Lake Forest Brands Choose OTT"
        subtitle="Local trust, national capability. The standards that drive OTT's reputation in Southern California and beyond."
      />

      {/* 7. Coverage */}
      <ServiceAreas
        regions={serviceRegions}
        title="From Lake Forest to All 50 States"
        subtitle="OTT's Lake Forest headquarters coordinates program deployment across every U.S. region — maintaining the same quality standards whether the event is in Orange County or New York."
        showNational
      />

      {/* 8. Testimonials */}
      <TestimonialsSection
        testimonials={testimonials}
        title="Trusted by Brands Nationwide"
        subtitle="From local Southern California programs to national OEM tours — clients who trust OTT program after program."
      />

      {/* 9. FAQ */}
      <FAQSection
        faqs={localFaqs}
        title="Lake Forest, CA — FAQ"
        subtitle="Common questions about OTT's Lake Forest operations and nationwide capabilities."
      />

      {/* 10. Final CTA */}
      <FinalCTA
        headline="Let's Talk About Your Program"
        subheadline="Connect with the OTT team in Lake Forest, CA. Whether your event is local or nationwide, we have the crew, the process, and the standard your program requires."
      />

      <Footer />
    </div>
  );
}
